import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from './use-toast';

interface NetworkConnection extends EventTarget {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkConnection;
  mozConnection?: NetworkConnection;
  webkitConnection?: NetworkConnection;
}

export interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  effectiveType?: string;
}

export const useNetworkStatus = (): NetworkStatus => {
  const { t } = useTranslation('common');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [effectiveType, setEffectiveType] = useState<string>();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast({
        title: t('network.restoredTitle'),
        description: t('network.restoredDescription'),
        duration: 3000,
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast({
        variant: 'destructive',
        title: t('network.offlineTitle'),
        description: t('network.offlineDescription'),
        duration: 5000,
      });
    };

    const handleConnectionChange = () => {
      const nav = navigator as NavigatorWithConnection;
      const connection = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
      
      if (connection) {
        setEffectiveType(connection.effectiveType);
        setIsSlowConnection(connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
        
        if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
          toast({
            title: t('network.slowTitle'),
            description: t('network.slowDescription'),
            duration: 4000,
          });
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    const nav = navigator as NavigatorWithConnection;
    const connection = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
      handleConnectionChange();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [t]);

  return {
    isOnline,
    isSlowConnection,
    effectiveType,
  };
};